import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { availableModules } from "@/types/modules";
import { Check, Link as LinkIcon, Plus, Loader2, RefreshCw, Camera, Shield } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usuariosAPI, rolesAPI, opcionesAPI, getMediaUrl, type Usuario, type Rol, type PuntoVenta } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { dataRefresh } from "@/lib/dataRefresh";
import { postSessionBroadcast } from "@/lib/sessionBroadcast";
import { PvPredeterminadoList } from "@/components/PvPredeterminadoList";
import { nextDefaultCodigo } from "@/lib/pvAsignado";
import { adoptCodigoFromListingId } from "@/lib/abmCreateIdentity";

interface UsuariosABMProps {
  selectedItem?: Usuario | null;
  onBack?: () => void;
  onUserCreated?: () => void;
}

const IMMORTAL_USER_USERNAMES = new Set(["admin"]);
const IMMORTAL_USER_CODIGOS = new Set(["usr001"]);

function isImmortalUsuario(
  user: { username?: string; codigo?: string; isImmortal?: boolean; is_immortal?: boolean } | null | undefined,
): boolean {
  if (!user) return false;
  const username = (user.username || "").trim().toLowerCase();
  const codigo = (user.codigo || "").trim().toLowerCase();
  // Exact identity only. Blank codigo is not system. Staff / rol Administrador is not.
  // Ignore isImmortal unless the row really is username admin / USR001.
  return (
    IMMORTAL_USER_USERNAMES.has(username) ||
    (codigo !== "" && IMMORTAL_USER_CODIGOS.has(codigo))
  );
}

function usuarioIsActive(
  user: { activo?: boolean; isActive?: boolean; is_active?: boolean } | null | undefined,
): boolean {
  if (!user) return true;
  if (typeof user.activo === "boolean") return user.activo;
  if (typeof user.isActive === "boolean") return user.isActive;
  if (typeof user.is_active === "boolean") return user.is_active;
  return true;
}

function usuarioHasAssociatedData(
  user: { hasAssociatedData?: boolean; has_associated_data?: boolean } | null | undefined,
): boolean {
  if (!user) return false;
  return Boolean(user.hasAssociatedData ?? user.has_associated_data);
}

function apiErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || !("status" in error)) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback;
  const e = error as {
    message?: unknown;
    details?: unknown;
    data?: Record<string, unknown>;
  };
  if (typeof e.message === "string" && e.message.trim() && e.message !== "Bad Request") {
    return e.message;
  }
  const details = e.details ?? e.data;
  if (typeof details === "string" && details.trim()) return details;
  if (Array.isArray(details) && typeof details[0] === "string") return details[0];
  if (details && typeof details === "object") {
    const d = details as Record<string, unknown>;
    if (typeof d.detail === "string" && d.detail.trim()) return d.detail;
    if (Array.isArray(d.detail) && typeof d.detail[0] === "string") return d.detail[0];
    if (typeof d.message === "string" && d.message.trim()) return d.message;
    const nested = firstDrfMessage(d);
    if (nested) return nested;
  }
  return fallback;
}

function firstDrfMessage(value: unknown, depth = 0): string | undefined {
  if (depth > 6 || value == null) return undefined;
  if (typeof value === "string" && value.trim() && value !== "Bad Request") return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstDrfMessage(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  if (typeof value === "object") {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      const found = firstDrfMessage(nested, depth + 1);
      if (found) return found;
    }
  }
  return undefined;
}

function usuarioLookup(
  user: { codigo?: string | null; username?: string; id?: number } | null | undefined,
): string {
  const codigo = (user?.codigo || "").trim();
  if (codigo) return codigo;
  const username = (user?.username || "").trim();
  if (username) return username;
  if (user?.id != null) return String(user.id);
  return "";
}

export function UsuariosABM({ selectedItem, onBack, onUserCreated }: UsuariosABMProps) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Data State
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [roles, setRoles] = useState<Rol[]>([]);
  const [puntosVenta, setPuntosVenta] = useState<PuntoVenta[]>([]);
  // Form State
  const emptyForm = {
    codigo: "",
    username: "",
    email: "",
    nombre: "",
    apellido: "",
    telefono: "",
    rol: "",
    modulosPermitidos: [] as number[],
    avatar_url: "",
    puntosVentaCodes: [] as string[],
    puntoVentaDefault: null as string | null,
    activo: true,
    mfaRequired: false,
    mfaEnrolled: false,
  };

  const [formData, setFormData] = useState<Partial<Usuario> & { puntosVentaCodes: string[] }>({
    codigo: "",
    username: "",
    email: "",
    nombre: "",
    apellido: "",
    telefono: "",
    rol: "",
    modulosPermitidos: [],
    avatar_url: "",
    puntosVentaCodes: [],
    puntoVentaDefault: null,
    mfaRequired: false,
    mfaEnrolled: false,
  });

  const [originalFormData, setOriginalFormData] = useState(formData);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAvatarUrlInput, setShowAvatarUrlInput] = useState(false);
  const [avatarUrlInput, setAvatarUrlInput] = useState("");
  const [selectedPuntoVenta, setSelectedPuntoVenta] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMfaResetDialog, setShowMfaResetDialog] = useState(false);
  const [showMfaDisableDialog, setShowMfaDisableDialog] = useState(false);
  const [password, setPassword] = useState(""); // Managed separately
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  /** Avatar elegido en alta (usuario aun no persistido); se sube tras POST exitoso. */
  const pendingAvatarFileRef = useRef<File | null>(null);

  const formId = (formData as { id?: number }).id;
  const loadedUsuario = formData.codigo
    ? usuarios.find((u) => u.codigo === formData.codigo)
    : selectedItem?.id != null
      ? usuarios.find((u) => u.id === selectedItem.id)
      : formId != null
        ? usuarios.find((u) => u.id === formId)
        : undefined;
  const immortal =
    isImmortalUsuario(loadedUsuario) ||
    isImmortalUsuario(selectedItem) ||
    isImmortalUsuario({ codigo: formData.codigo, username: formData.username });
  const persistedCodigo = (
    formData.codigo ||
    loadedUsuario?.codigo ||
    selectedItem?.codigo ||
    ""
  ).trim();
  const persistedId = (formData as { id?: number }).id ?? selectedItem?.id;
  const isPersisted = Boolean(persistedId || persistedCodigo);
  const persistedLookup = isPersisted
    ? usuarioLookup({
        codigo: persistedCodigo || formData.codigo,
        username: formData.username || selectedItem?.username,
        id: persistedId,
      })
    : "";
  const willDeactivate =
    usuarioHasAssociatedData(formData) ||
    usuarioHasAssociatedData(loadedUsuario) ||
    usuarioHasAssociatedData(selectedItem);
  const isInactive = isPersisted && !usuarioIsActive(formData);

  // Initial Data Load
  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData, pvsData] = await Promise.all([
        usuariosAPI.list(),
        rolesAPI.list(),
        opcionesAPI.listPuntosVenta(),
      ]);
      setUsuarios(usersData);
      setRoles(rolesData);
      setPuntosVenta(pvsData);
      const adoptCodigo = (
        prev: Partial<Usuario> & { puntosVentaCodes: string[] },
      ): Partial<Usuario> & { puntosVentaCodes: string[] } => {
        const match = adoptCodigoFromListingId(
          prev.codigo,
          usersData,
          selectedItem?.id ?? prev.id,
        );
        if (match?.codigo) return { ...prev, codigo: match.codigo, id: match.id };
        return prev;
      };
      setFormData(adoptCodigo);
      setOriginalFormData(adoptCodigo);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const goToListado = () => {
    setShowDeleteDialog(false);
    if (onBack) onBack();
    else if (onUserCreated) onUserCreated();
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load selectedItem when passed from parent (wait for roles to load)
  useEffect(() => {
    if (loading) return; // Wait for data to load first

    if (selectedItem) {
      selectUsuario(selectedItem);
    } else {
      // Reset form for new item
      pendingAvatarFileRef.current = null;
      setFormData(emptyForm);
      setOriginalFormData(emptyForm);
      setPassword("");
      setHasChanges(false);
      setSelectedPuntoVenta("");
    }
  }, [selectedItem, loading, roles]);

  useEffect(() => {
    if (loading) return;
    const lookup = usuarioLookup(selectedItem);
    if (!lookup) return;
    let cancelled = false;
    (async () => {
      try {
        const fresh = await usuariosAPI.get(lookup);
        if (cancelled) return;
        selectUsuario(fresh);
      } catch (error) {
        if (cancelled) return;
        if (apiErrorStatus(error) === 404) {
          goToListado();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // GET of a gone user must leave the editor; don't toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.id, selectedItem?.username, selectedItem?.codigo, loading]);

  // Update modules when role changes
  const updateModulosForRole = (rolNombre: string) => {
    const rol = roles.find(r => r.nombre === rolNombre);
    if (!rol) return [];
    // Convert granular permissions to list of module IDs
    return rol.modulosPermitidos
      .filter(p => p.permission !== 'none')
      .map(p => p.moduleId);
  };

  const handleUsuarioChange = (value: string) => {
    const next = { ...formData, username: value };
    setFormData(next);
    checkForChanges(next);
  };

  const selectUsuario = (usuario: Usuario) => {
    // Determine assigned PVs based on available data
    // Backend creates separate PuntosVenta list, so we map them to codes
    const assignedPVCodes = usuario.puntosVenta?.map((pv: any) => pv.codigoPV || pv.codigo) || [];
    const defaultCode =
      usuario.puntoVentaDefault ||
      usuario.punto_venta_default ||
      nextDefaultCodigo(assignedPVCodes);

    // Populate modulosPermitidos from the user's role
    const modulosPermitidos = usuario.rol ? updateModulosForRole(usuario.rol) : [];

    // Convert avatar URL - backend returns avatarUrl, we store as avatar_url
    const avatarUrl = (usuario as any).avatarUrl || usuario.avatar_url;

    const loadedData = {
      ...usuario,
      avatar_url: avatarUrl ? getMediaUrl(avatarUrl) : null,
      puntosVentaCodes: assignedPVCodes,
      puntoVentaDefault: defaultCode,
      modulosPermitidos: modulosPermitidos,
      activo: usuarioIsActive(usuario),
      mfaRequired: Boolean(usuario.mfaRequired ?? usuario.mfa_required),
      mfaEnrolled: Boolean(usuario.mfaEnrolled ?? usuario.mfa_enrolled),
    };

    pendingAvatarFileRef.current = null;
    setFormData(loadedData);
    setOriginalFormData(loadedData);
    setPassword(""); // Clear password field on load
    setHasChanges(false);
    setSelectedPuntoVenta("");

    // Refresh PVs list to ensure latest assignment status is known
    opcionesAPI.listPuntosVenta().then(setPuntosVenta);
  };

  const checkForChanges = (
    data: typeof formData,
    newPass?: string,
  ) => {
    // Compare key fields explicitly to avoid issues with extra properties from API
    const fieldsToCompare = ['username', 'email', 'nombre', 'apellido', 'telefono', 'rol', 'avatar_url', 'activo', 'mfaRequired'];
    const fieldChanged = fieldsToCompare.some(field =>
      (data as any)[field] !== (originalFormData as any)[field]
    );

    // Compare puntosVentaCodes arrays
    const pvChanged = JSON.stringify(data.puntosVentaCodes || []) !== JSON.stringify(originalFormData.puntosVentaCodes || []);
    const defaultChanged =
      (data.puntoVentaDefault || data.punto_venta_default || "") !==
      (originalFormData.puntoVentaDefault || originalFormData.punto_venta_default || "");

    const passChanged = !!newPass;
    setHasChanges(fieldChanged || pvChanged || defaultChanged || passChanged);
  };

  const handleInputChange = (field: keyof Usuario, value: any) => {
    const newData = { ...formData, [field]: value };

    // If changing role, update modulosPermitidos
    if (field === "rol") {
      newData.modulosPermitidos = updateModulosForRole(value);
    }

    setFormData(newData);
    checkForChanges(newData, password);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Por favor seleccione un archivo de imagen");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no debe superar los 5MB");
      return;
    }

    // Usuario ya persistido: subir al servidor de inmediato (mismo flujo que Perfil).
    if (isPersisted && (formData.username || persistedLookup)) {
      if (!formData.username) {
        toast.error("Falta nombre de usuario para subir el avatar");
        return;
      }
      setUploadingAvatar(true);
      try {
        const result = await usuariosAPI.uploadAvatar(formData.username, file);
        const avatarUrl = result.avatar_url || (result as any).avatarUrl;

        if (result.success && avatarUrl) {
          const fullUrl = getMediaUrl(avatarUrl);
          const newData = { ...formData, avatar_url: fullUrl || avatarUrl };
          setFormData(newData);
          checkForChanges(newData, password);
          dataRefresh.emit("userAvatar");
          postSessionBroadcast({
            type: "avatar:updated",
            userId: formData.id,
            codigo: formData.codigo,
            username: formData.username,
          });
          toast.success("Avatar subido exitosamente");
        } else {
          toast.error("Error al subir el avatar");
        }
      } catch (err) {
        console.error("Avatar upload error:", err);
        toast.error(err instanceof Error ? err.message : "Error al subir la imagen");
      } finally {
        setUploadingAvatar(false);
      }
      return;
    }

    // Alta: el usuario aún no existe en BD — evitar POST a upload-avatar (404). Guardar archivo y preview.
    pendingAvatarFileRef.current = file;
    if (formData.avatar_url?.startsWith("blob:")) {
      URL.revokeObjectURL(formData.avatar_url);
    }
    const imageUrl = URL.createObjectURL(file);
    const newData = { ...formData, avatar_url: imageUrl };
    setFormData(newData);
    checkForChanges(newData, password);
    toast.info("Imagen lista. Se subirá al guardar el usuario.");
  };

  const handleAvatarUrlSubmit = () => {
    if (avatarUrlInput.trim()) {
      const newData = { ...formData, avatar_url: avatarUrlInput };
      setFormData(newData);
      checkForChanges(newData, password);
      setShowAvatarUrlInput(false);
      setAvatarUrlInput("");
      toast.success("URL de avatar guardada");
    }
  };

  // Punto Venta Management
  const addPuntoVenta = () => {
    if (!selectedPuntoVenta) {
      toast.error("Seleccione un punto de venta");
      return;
    }

    if (formData.puntosVentaCodes.includes(selectedPuntoVenta)) return;

    // Logic: A user can have many PVs (one per sucursal)
    // Check constraint: One PV per sucursal for THIS user
    const selectedPV = puntosVenta.find(pv => pv.codigo === selectedPuntoVenta);
    if (!selectedPV) return;

    // Check if user already has a PV for this sucursal
    const userPVs = puntosVenta.filter(pv => formData.puntosVentaCodes.includes(pv.codigo));
    if (userPVs.some(pv => pv.sucursal === selectedPV.sucursal)) {
      toast.error(`Ya tiene un punto de venta asignado para la sucursal ${selectedPV.sucursalNombre || selectedPV.sucursal_nombre || 'seleccionada'}`);
      return;
    }

    const previousDefault =
      formData.puntoVentaDefault || formData.punto_venta_default;
    const newData = {
      ...formData,
      puntosVentaCodes: [...formData.puntosVentaCodes, selectedPuntoVenta],
      puntoVentaDefault: nextDefaultCodigo(
        [...formData.puntosVentaCodes, selectedPuntoVenta],
        previousDefault,
      ),
    };
    setFormData(newData);
    checkForChanges(newData, password);
    setSelectedPuntoVenta("");
  };

  const removePuntoVenta = (codigoPV: string) => {
    const nextCodes = formData.puntosVentaCodes.filter(c => c !== codigoPV);
    const newData = {
      ...formData,
      puntosVentaCodes: nextCodes,
      puntoVentaDefault: nextDefaultCodigo(
        nextCodes,
        formData.puntoVentaDefault || formData.punto_venta_default,
      ),
    };
    setFormData(newData);
    checkForChanges(newData, password);
  };

  const setPredeterminado = (codigoPV: string) => {
    const newData = { ...formData, puntoVentaDefault: codigoPV };
    setFormData(newData);
    checkForChanges(newData, password);
  };

  const getAvailablePuntosVenta = () => {
    return puntosVenta.filter(pv => {
      if (!pv.activo) return false;
      if (formData.puntosVentaCodes.includes(pv.codigo)) return false;
      return true;
    });
  };

  const pvListItems = formData.puntosVentaCodes.map((pvCode) => {
    const pv = puntosVenta.find((p) => p.codigo === pvCode);
    return {
      codigo: pvCode,
      nombre: pv?.nombre || pvCode,
      sucursalLabel: pv?.sucursal_nombre || pv?.sucursalNombre || "",
    };
  });
  const listDefaultCodigo =
    formData.puntoVentaDefault ||
    formData.punto_venta_default ||
    nextDefaultCodigo(formData.puntosVentaCodes);

  const handleSave = async () => {
    if (!formData.username || !formData.email || !formData.nombre || !formData.apellido || !formData.rol) {
      toast.error("Complete todos los campos requeridos");
      return;
    }

    setProcessing(true);
    try {
      const isNewUser = !isPersisted;

      const avatarIsBlobPreview =
        typeof formData.avatar_url === "string" && formData.avatar_url.startsWith("blob:");

      const assignedCodes = formData.puntosVentaCodes || [];
      const puntoVentaDefault = nextDefaultCodigo(
        assignedCodes,
        formData.puntoVentaDefault || formData.punto_venta_default || null,
      );

      const payload: Record<string, unknown> = {
        username: formData.username,
        email: formData.email,
        nombre: formData.nombre,
        apellido: formData.apellido,
        telefono: formData.telefono || null,
        rol: formData.rol || null,
        // No enviar URL blob al backend; en alta el archivo va en segundo paso.
        avatar_url: avatarIsBlobPreview ? null : formData.avatar_url || null,
        is_active: formData.activo ?? true,
        puntosVentaCodes: assignedCodes,
        puntoVentaDefault,
        mfaRequired: Boolean(formData.mfaRequired ?? formData.mfa_required),
      };

      if (password) {
        payload.password = password;
      }

      if (isNewUser) {
        if (!password) {
          payload.password = "password123"; // Default or prompt
        }
        const created = await usuariosAPI.create(payload as Parameters<typeof usuariosAPI.create>[0]);
        toast.success("Usuario creado exitosamente");

        const pendingFile = pendingAvatarFileRef.current;
        pendingAvatarFileRef.current = null;
        if (avatarIsBlobPreview && formData.avatar_url) {
          URL.revokeObjectURL(formData.avatar_url);
        }

        if (pendingFile && created.username) {
          setUploadingAvatar(true);
          try {
            const result = await usuariosAPI.uploadAvatar(created.username, pendingFile);
            const avatarUrl = result.avatar_url || (result as { avatarUrl?: string }).avatarUrl;
            if (result.success && avatarUrl) {
              dataRefresh.emit("userAvatar");
              postSessionBroadcast({
                type: "avatar:updated",
                userId: created.id,
                codigo: created.codigo,
                username: created.username,
              });
              toast.success("Avatar subido correctamente");
            }
          } catch (avatarErr) {
            console.error("Avatar post-create error:", avatarErr);
            toast.error(
              avatarErr instanceof Error
                ? `Usuario creado, pero el avatar falló: ${avatarErr.message}. Puede subirlo al editar el usuario o desde Perfil.`
                : "Usuario creado, pero no se pudo subir el avatar. Puede reintentarlo al editar."
            );
          } finally {
            setUploadingAvatar(false);
          }
        }
      } else {
        await usuariosAPI.update(persistedLookup || formData.codigo!, payload as Parameters<typeof usuariosAPI.update>[1]);
        toast.success("Usuario actualizado exitosamente");
        postSessionBroadcast({
          type: "user:updated",
          userId: formData.id,
          codigo: formData.codigo,
          username: formData.username,
        });
      }

      // Reset form
      const nextEmpty = {
        codigo: "",
        username: "",
        email: "",
        nombre: "",
        apellido: "",
        telefono: "",
        rol: "",
        modulosPermitidos: [] as number[],
        avatar_url: "",
        puntosVentaCodes: [] as string[],
        puntoVentaDefault: null as string | null,
        activo: true,
        mfaRequired: false,
        mfaEnrolled: false,
      };
      setFormData(nextEmpty);
      setOriginalFormData(nextEmpty);
      setPassword("");
      setHasChanges(false);
      setSelectedPuntoVenta("");

      // Refresh list
      loadData();

      if (onUserCreated) onUserCreated();

    } catch (error: unknown) {
      console.error("Save error:", error);
      toast.error(apiErrorMessage(error, "Error al guardar usuario"));
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (immortal) {
      setShowDeleteDialog(false);
      return;
    }
    const lookup = persistedLookup;
    if (!lookup) {
      toast.error("Seleccioná un usuario para eliminar");
      setShowDeleteDialog(false);
      return;
    }

    setProcessing(true);
    try {
      const result = await usuariosAPI.delete(lookup);
      const deactivated = Boolean(
        result &&
          typeof result === "object" &&
          (result.deactivated || (result as { code?: string }).code === "user_deactivated"),
      );
      toast.success(
        deactivated
          ? "Usuario desactivado. No podrá iniciar sesión; el historial se conserva."
          : "Usuario eliminado",
      );
      goToListado();
    } catch (error: unknown) {
      if (apiErrorStatus(error) === 404) {
        goToListado();
        return;
      }
      toast.error(apiErrorMessage(error, "Error al eliminar usuario"));
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    if (immortal) return;
    const lookup = persistedLookup;
    if (!lookup) {
      toast.error("Seleccioná un usuario para reactivar");
      return;
    }
    setProcessing(true);
    try {
      await usuariosAPI.update(lookup, { is_active: true } as Partial<Usuario>);
      const next = { ...formData, activo: true, isActive: true, is_active: true };
      setFormData(next);
      setOriginalFormData(next);
      setHasChanges(false);
      toast.success("Usuario reactivado");
      loadData();
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Error al reactivar usuario"));
    } finally {
      setProcessing(false);
    }
  };

  const mfaRequiredOn = Boolean(formData.mfaRequired ?? formData.mfa_required);
  const mfaEnrolledOn = Boolean(formData.mfaEnrolled ?? formData.mfa_enrolled);

  const applyMfaFlags = (required: boolean, enrolled: boolean) => {
    const next = {
      ...formData,
      mfaRequired: required,
      mfa_required: required,
      mfaEnrolled: enrolled,
      mfa_enrolled: enrolled,
    };
    setFormData(next);
    setOriginalFormData(next);
    setHasChanges(false);
  };

  const handleConfirmMfaReset = async () => {
    const lookup = persistedLookup;
    if (!lookup) {
      toast.error("Seleccioná un usuario");
      setShowMfaResetDialog(false);
      return;
    }
    setProcessing(true);
    try {
      await usuariosAPI.resetMfa(lookup);
      applyMfaFlags(true, false);
      toast.success("MFA reseteado. En el próximo login verá el wizard.");
      loadData();
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Error al resetear MFA"));
    } finally {
      setProcessing(false);
      setShowMfaResetDialog(false);
    }
  };

  const handleConfirmMfaDisable = async () => {
    const lookup = persistedLookup;
    if (!lookup) {
      toast.error("Seleccioná un usuario");
      setShowMfaDisableDialog(false);
      return;
    }
    setProcessing(true);
    try {
      await usuariosAPI.disableMfa(lookup);
      applyMfaFlags(false, false);
      toast.success("MFA deshabilitado. Puede iniciar sesión solo con contraseña.");
      loadData();
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Error al deshabilitar MFA"));
    } finally {
      setProcessing(false);
      setShowMfaDisableDialog(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end mb-2 gap-2 items-center">
        {isInactive && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 mr-auto">
            Inactivo
          </span>
        )}
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Recargar
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={formData.codigo}
            readOnly
            tabIndex={-1}
            className="w-full px-4 py-2 border border-input bg-muted rounded-lg focus:outline-none text-sm"
            placeholder="Auto-generado"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Usuario</label>
          <input
            type="text"
            value={formData.username}
            onChange={(e) => handleUsuarioChange(e.target.value)}
            placeholder="Nombre de usuario..."
            tabIndex={1}
            autoComplete="off"
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contraseña</label>
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              checkForChanges(formData, e.target.value);
            }}
            tabIndex={2}
            placeholder={isPersisted ? "Dejar vacío para mantener" : "Requerido"}
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            tabIndex={3}
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nombre</label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => handleInputChange("nombre", e.target.value)}
            tabIndex={4}
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Apellido</label>
          <input
            type="text"
            value={formData.apellido}
            onChange={(e) => handleInputChange("apellido", e.target.value)}
            tabIndex={5}
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Teléfono</label>
          <input
            type="text"
            value={formData.telefono || ""}
            onChange={(e) => handleInputChange("telefono", e.target.value)}
            tabIndex={6}
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Rol</label>
          <select
            value={formData.rol || ""}
            onChange={(e) => handleInputChange("rol", e.target.value)}
            tabIndex={7}
            className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          >
            <option value="">Seleccionar rol...</option>
            {roles.map((rol) => (
              <option key={rol.codigo} value={rol.nombre}>
                {rol.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Avatar + MFA: misma fila, mismas dimensiones (50/50, altura = la más alta) */}
      <div className="border-t border-border pt-4">
        <div className="grid grid-cols-2 gap-4 items-stretch">
          <div className="rounded-lg border border-border p-4 h-full min-h-[11rem] flex flex-col">
            <label className="block text-sm font-medium mb-3">Avatar del Usuario</label>
            <div className="flex items-start gap-4 flex-1">
              <label htmlFor="avatar-upload" className="cursor-pointer relative group flex-shrink-0">
                <div className="w-24 h-24 rounded-full bg-muted border-2 border-border overflow-hidden flex items-center justify-center">
                  {formData.avatar_url ? (
                    <img
                      src={formData.avatar_url}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl text-muted-foreground">
                      {formData.nombre?.[0]?.toUpperCase() || formData.username?.[0]?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="h-8 w-8 text-white" />
                </div>
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                )}
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              <div className="flex-1 space-y-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAvatarUrlInput(!showAvatarUrlInput)}
                    tabIndex={9}
                    className="flex items-center gap-2"
                  >
                    <LinkIcon className="h-4 w-4" />
                    <span className="text-xs">Usar URL</span>
                  </Button>
                </div>
                {showAvatarUrlInput && (
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={avatarUrlInput}
                      onChange={(e) => setAvatarUrlInput(e.target.value)}
                      placeholder="https://ejemplo.com/avatar.jpg"
                      tabIndex={10}
                      className="flex-1 px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    />
                    <Button type="button" size="sm" onClick={handleAvatarUrlSubmit} tabIndex={11}>
                      OK
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border p-4 h-full min-h-[11rem] flex flex-col">
            <label className="block text-sm font-medium mb-3 flex items-center gap-2">
              <Shield className="h-4 w-4 shrink-0" />
              Autenticación en dos pasos (MFA)
            </label>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <Switch
                checked={mfaRequiredOn}
                disabled={processing || mfaEnrolledOn}
                onCheckedChange={(checked) => handleInputChange("mfaRequired" as keyof Usuario, checked)}
              />
              <span className="text-sm">Habilitar MFA</span>
              {mfaEnrolledOn ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Enrolado
                </span>
              ) : mfaRequiredOn ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Pendiente de enrolamiento
                </span>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              El usuario escaneará el QR en su próximo inicio de sesión, en su propio
              teléfono. Acá no se muestra ningún código QR ni secreto.
            </p>
            {isPersisted && (mfaRequiredOn || mfaEnrolledOn) && (
              <div className="flex flex-wrap gap-2 mt-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={processing}
                  onClick={() => setShowMfaResetDialog(true)}
                >
                  Resetear MFA
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={processing}
                  onClick={() => setShowMfaDisableDialog(true)}
                >
                  Deshabilitar MFA
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {formData.rol && (
        <div className="mt-6 border-t border-border pt-4">
          <label className="block text-sm font-medium mb-2">Módulos Permitidos (Basado en Rol)</label>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 opacity-90">
            {availableModules.map((module) => {
              const Icon = module.icon;
              const isEnabled = formData.modulosPermitidos?.includes(module.id);

              return (
                <div
                  key={module.id}
                  className="relative flex flex-col items-center gap-2"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${module.gradient} flex items-center justify-center shadow-lg transition-all ${isEnabled ? 'ring-2 ring-primary ring-offset-2' : 'opacity-30 grayscale'}`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <span className={`text-xs text-center max-w-[80px] line-clamp-2 ${!isEnabled ? 'opacity-50' : ''}`}>
                    {module.name}
                  </span>
                  {isEnabled && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Puntos de Venta: 1 por sucursal, compartido. No se crea PV al alta de usuario. */}
      <div className="mt-6 border-t border-border pt-4">
        <label className="block text-sm font-medium mb-1">Puntos de Venta Asignados</label>
        <p className="text-xs text-muted-foreground mb-3">
          Un PV por sucursal, compartido. Varios usuarios pueden tener el mismo como Predeterminado.
        </p>
        <div className="space-y-3">
          <div className="flex gap-2">
            <select
              value={selectedPuntoVenta}
              onChange={(e) => setSelectedPuntoVenta(e.target.value)}
              tabIndex={12}
              className="flex-1 px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            >
              <option value="">Seleccionar punto de venta...</option>
              {getAvailablePuntosVenta().map((pv) => (
                <option key={pv.codigo} value={pv.codigo}>
                  {pv.nombre} - {pv.sucursalNombre || pv.sucursal_nombre || 'Sucursal desconocida'}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              onClick={addPuntoVenta}
              disabled={!selectedPuntoVenta}
              tabIndex={13}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Agregar
            </Button>
          </div>

          {pvListItems.length > 0 ? (
            <PvPredeterminadoList
              items={pvListItems}
              defaultCodigo={listDefaultCodigo}
              onDefaultChange={setPredeterminado}
              onRemove={removePuntoVenta}
            />
          ) : (
            <div className="text-center py-4 text-sm text-muted-foreground">
              No hay puntos de venta asignados
            </div>
          )}
        </div>
      </div>

      <FormFooter
        onBack={onBack}
        onDelete={handleDelete}
        deleteDisabled={!persistedLookup || immortal || processing}
        deleteTabIndex={15}
        extra={
          isInactive && !immortal ? (
            <Button
              onClick={handleReactivate}
              variant="outline"
              disabled={processing}
              tabIndex={16}
            >
              Reactivar
            </Button>
          ) : undefined
        }
        onSave={handleSave}
        saveDisabled={!hasChanges || processing}
        saveTabIndex={14}
        saving={processing}
      />

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {willDeactivate ? "Desactivar usuario" : "Eliminar usuario"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {willDeactivate
                ? `¿Desactivar a ${formData.username}? No podrá iniciar sesión, pero el historial se conserva.`
                : `¿Estás seguro que deseas eliminar el usuario ${formData.username}? Esta acción es permanente.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} variant="destructive">
              {willDeactivate ? "Desactivar" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showMfaResetDialog} onOpenChange={setShowMfaResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetear MFA</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Resetear MFA para {formData.username}? Se borra el authenticator
              actual. En el próximo inicio de sesión deberá escanear un QR nuevo.
              MFA sigue siendo obligatorio. No se muestra ningún código QR acá.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowMfaResetDialog(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmMfaReset}>
              Resetear MFA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showMfaDisableDialog} onOpenChange={setShowMfaDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deshabilitar MFA</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Deshabilitar MFA para {formData.username}? Es un último recurso
              (teléfono perdido, códigos extraviados). Podrá iniciar sesión solo
              con usuario y contraseña. El secreto se elimina. No se muestra
              ningún código QR.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowMfaDisableDialog(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmMfaDisable} variant="destructive">
              Deshabilitar MFA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}